/**
 * CalendarWeekView — the central calendar grid.
 *
 * Day view = 1 column, week view = 7 columns (Mon–Sun). Other view modes
 * (month / quarter / year) currently render the same week grid; build them
 * out as the data needs require.
 *
 * Layout:
 *   - sticky header row with column dates
 *   - thin all-day strip below the header
 *   - main grid: hour rows on the left, scrollable column area on the right
 *   - blocks absolutely positioned within their day column
 *
 * Drag-and-drop:
 *   - mousedown on a block records a potential drag (original position,
 *     duration, cursor-to-block offset in minutes)
 *   - mousemove past a 5px threshold flips to drag mode; a ghost tile
 *     renders at the target column + 15-min-snapped time
 *   - mouseup commits via rescheduleBlock (optimistic update + PATCH);
 *     mouseup below the threshold falls through to "select block"
 *   - Escape cancels the in-flight drag
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isToday, isSameDay, format } from 'date-fns'
import { useStudioStore } from '@/stores/useStudioStore'
import {
  computeBlockGridPosition, deriveEnds, DAY_START_HOUR, DAY_END_HOUR,
  GRID_PX_HEIGHT, PX_PER_HOUR, PX_PER_MINUTE, parseServerDate, weekDates,
  formatHourLabel, snapToSlot,
} from '@/features/studio/lib/time'
import type { StudioBlock } from '@/lib/studio'
import { BlockTile } from './BlockTile'

const HOUR_LABEL_WIDTH = 56
const DRAG_THRESHOLD_PX = 5

// Drag state — null when no mousedown is in flight. `committed` flips true
// once the cursor has moved past the threshold; before that, mouseup
// resolves as a click.
interface DragState {
  blockId: string
  universeId: string
  projectId: string
  durationMinutes: number
  originalStartsAt: Date
  /** Cursor offset from the block's top edge, in minutes. Preserved during the drag so the cursor stays on the same spot of the tile. */
  grabOffsetMinutes: number
  startMouseX: number
  startMouseY: number
  targetDayKey: string
  /** Target start, in minutes-of-day (snapped to 15). */
  targetMinutesOfDay: number
  committed: boolean
}

export function CalendarWeekView() {
  const blocks = useStudioStore((s) => s.blocks)
  const universes = useStudioStore((s) => s.universes)
  const calendarView = useStudioStore((s) => s.calendarView)
  const calendarAnchor = useStudioStore((s) => s.calendarAnchor)
  const activeBlockId = useStudioStore((s) => s.activeBlockId)
  const rescheduleBlock = useStudioStore((s) => s.rescheduleBlock)
  const setActiveUniverse = useStudioStore((s) => s.setActiveUniverse)
  const setActiveProject = useStudioStore((s) => s.setActiveProject)
  const setActiveBlock = useStudioStore((s) => s.setActiveBlock)

  const anchor = new Date(calendarAnchor)
  const days = useMemo(() => {
    return calendarView === 'day' ? [anchor] : weekDates(anchor)
    // calendarAnchor change triggers; anchor is derived each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarAnchor, calendarView])

  const universeColorMap = useMemo(() => {
    const m = new Map<string, string | null>()
    for (const u of universes) m.set(u.id, u.color)
    return m
  }, [universes])

  const { allDayByDay, timedByDay } = useMemo(() => {
    const allDay: Record<string, StudioBlock[]> = {}
    const timed: Record<string, StudioBlock[]> = {}
    for (const d of days) {
      const key = format(d, 'yyyy-MM-dd')
      allDay[key] = []
      timed[key] = []
    }
    for (const b of blocks) {
      const starts = parseServerDate(b.starts_at)
      if (!starts) continue
      const key = format(starts, 'yyyy-MM-dd')
      if (!(key in timed)) continue
      if (b.all_day) {
        allDay[key].push(b)
      } else {
        timed[key].push(b)
      }
    }
    return { allDayByDay: allDay, timedByDay: timed }
  }, [blocks, days])

  // Auto-scroll to current hour on view/anchor change.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!scrollRef.current) return
    const now = new Date()
    const targetHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR - 4, now.getHours() - 2))
    scrollRef.current.scrollTop = (targetHour - DAY_START_HOUR) * PX_PER_HOUR
  }, [calendarView, calendarAnchor])

  // Live "now" indicator (minutes from grid start; null when outside window)
  const nowMinutes = (() => {
    const now = new Date()
    const m = now.getHours() * 60 + now.getMinutes() - DAY_START_HOUR * 60
    return m >= 0 && m <= (DAY_END_HOUR - DAY_START_HOUR) * 60 ? m : null
  })()

  const hours = Array.from(
    { length: DAY_END_HOUR - DAY_START_HOUR + 1 },
    (_, i) => DAY_START_HOUR + i,
  )

  // ─── Drag handling ───────────────────────────────────────────────────

  const [drag, setDrag] = useState<DragState | null>(null)

  // Pixel → minute-of-day under the cursor, accounting for scroll.
  // Returns null if the scroll container isn't mounted yet.
  const cursorToMinuteOfDay = useCallback((clientY: number): number | null => {
    const scroll = scrollRef.current
    if (!scroll) return null
    const rect = scroll.getBoundingClientRect()
    const yIntoGrid = clientY - rect.top + scroll.scrollTop
    return DAY_START_HOUR * 60 + yIntoGrid / PX_PER_MINUTE
  }, [])

  const cursorToDayIndex = useCallback((clientX: number): number => {
    const scroll = scrollRef.current
    if (!scroll) return 0
    const rect = scroll.getBoundingClientRect()
    const colsLeft = rect.left + HOUR_LABEL_WIDTH
    const colsWidth = rect.width - HOUR_LABEL_WIDTH
    if (colsWidth <= 0) return 0
    const colWidth = colsWidth / days.length
    const idx = Math.floor((clientX - colsLeft) / colWidth)
    return Math.max(0, Math.min(days.length - 1, idx))
  }, [days.length])

  const onBlockMouseDown = useCallback((block: StudioBlock, e: React.MouseEvent) => {
    // All-day blocks live outside the timed grid; selecting them is fine,
    // dragging is out of scope for v1.
    if (block.all_day || !block.starts_at) {
      setActiveUniverse(block.universe_id)
      setActiveProject(block.project_id)
      setActiveBlock(block.id)
      return
    }
    // Only left-click drives drag.
    if (e.button !== 0) return
    e.preventDefault()

    const starts = new Date(block.starts_at)
    const ends = block.ends_at
      ? new Date(block.ends_at)
      : deriveEnds(starts, block.duration_minutes)
    const durationMinutes = Math.max(
      1,
      Math.round((ends.getTime() - starts.getTime()) / 60000),
    )

    const blockStartMinute = starts.getHours() * 60 + starts.getMinutes()
    const cursorMinute = cursorToMinuteOfDay(e.clientY) ?? blockStartMinute
    const grabOffsetMinutes = cursorMinute - blockStartMinute

    setDrag({
      blockId: block.id,
      universeId: block.universe_id,
      projectId: block.project_id,
      durationMinutes,
      originalStartsAt: starts,
      grabOffsetMinutes,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      targetDayKey: format(starts, 'yyyy-MM-dd'),
      targetMinutesOfDay: blockStartMinute,
      committed: false,
    })
  }, [cursorToMinuteOfDay, setActiveBlock, setActiveProject, setActiveUniverse])

  useEffect(() => {
    if (!drag) return

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - drag.startMouseX
      const dy = e.clientY - drag.startMouseY
      const committed = drag.committed || Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX
      if (!committed) return

      const dayIdx = cursorToDayIndex(e.clientX)
      const targetDate = days[dayIdx]
      const targetDayKey = format(targetDate, 'yyyy-MM-dd')

      const cursorMin = cursorToMinuteOfDay(e.clientY)
      if (cursorMin === null) return
      let blockStart = cursorMin - drag.grabOffsetMinutes
      blockStart = snapToSlot(blockStart)
      // Clamp so the whole block stays within the visible window.
      const minStart = DAY_START_HOUR * 60
      const maxStart = DAY_END_HOUR * 60 - drag.durationMinutes
      blockStart = Math.max(minStart, Math.min(maxStart, blockStart))

      setDrag((prev) =>
        prev
          ? { ...prev, committed: true, targetDayKey, targetMinutesOfDay: blockStart }
          : null,
      )
    }

    const onMouseUp = () => {
      const current = drag
      setDrag(null)
      if (!current) return

      if (!current.committed) {
        // Below threshold = treat as a click → select the block.
        setActiveUniverse(current.universeId)
        setActiveProject(current.projectId)
        setActiveBlock(current.blockId)
        return
      }

      const target = days.find((d) => format(d, 'yyyy-MM-dd') === current.targetDayKey)
      if (!target) return

      const newStarts = new Date(target)
      newStarts.setHours(0, 0, 0, 0)
      newStarts.setMinutes(current.targetMinutesOfDay)
      const newEnds = new Date(newStarts.getTime() + current.durationMinutes * 60000)

      if (newStarts.getTime() === current.originalStartsAt.getTime()) return
      void rescheduleBlock(current.blockId, newStarts, newEnds)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrag(null)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('keydown', onKeyDown)
    if (drag.committed) {
      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [drag, days, cursorToDayIndex, cursorToMinuteOfDay, rescheduleBlock, setActiveBlock, setActiveProject, setActiveUniverse])

  // Pre-compute ghost geometry so each column can decide whether to render it.
  const ghost = useMemo(() => {
    if (!drag || !drag.committed) return null
    const target = days.find((d) => format(d, 'yyyy-MM-dd') === drag.targetDayKey)
    if (!target) return null
    const newStarts = new Date(target)
    newStarts.setHours(0, 0, 0, 0)
    newStarts.setMinutes(drag.targetMinutesOfDay)
    const newEnds = new Date(newStarts.getTime() + drag.durationMinutes * 60000)
    const pos = computeBlockGridPosition(newStarts, newEnds)
    return {
      dayKey: drag.targetDayKey,
      topPx: pos.topPx,
      heightPx: pos.heightPx,
      timeLabel: `${String(newStarts.getHours()).padStart(2, '0')}:${String(newStarts.getMinutes()).padStart(2, '0')}`,
    }
  }, [drag, days])

  const draggedBlock = drag ? blocks.find((b) => b.id === drag.blockId) : null

  return (
    <div className="h-full flex flex-col bg-neutral-950">
      {/* Header row */}
      <div className="flex flex-shrink-0 border-b border-neutral-800 bg-neutral-900">
        <div style={{ width: HOUR_LABEL_WIDTH }} className="border-r border-neutral-800" />
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={
              'flex-1 px-2 py-2 text-center border-r border-neutral-800 last:border-r-0 ' +
              (isToday(d) ? 'bg-neutral-800/40' : '')
            }
          >
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-light">
              {format(d, 'EEE')}
            </div>
            <div
              className={
                'text-lg font-light mt-0.5 ' +
                (isToday(d) ? 'text-blue-400' : 'text-neutral-200')
              }
            >
              {format(d, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* All-day strip */}
      <div className="flex flex-shrink-0 border-b border-neutral-800 bg-neutral-900/50 min-h-[28px]">
        <div
          style={{ width: HOUR_LABEL_WIDTH }}
          className="border-r border-neutral-800 px-2 py-1 text-[9px] uppercase tracking-wide text-neutral-500 font-light"
        >
          all-day
        </div>
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd')
          const items = allDayByDay[key] || []
          return (
            <div
              key={`ad-${key}`}
              className={
                'flex-1 px-1 py-1 border-r border-neutral-800 last:border-r-0 flex flex-wrap gap-1 ' +
                (isToday(d) ? 'bg-neutral-800/20' : '')
              }
            >
              {items.map((b) => (
                <div
                  key={b.id}
                  onClick={() => useStudioStore.getState().setActiveBlock(b.id)}
                  className="px-1.5 py-0.5 text-[10px] rounded bg-neutral-800 border border-neutral-700 text-neutral-200 truncate cursor-pointer hover:bg-neutral-700 max-w-full"
                  title={b.title}
                >
                  {b.title}
                </div>
              ))}
            </div>
          )
        })}
      </div>

      {/* Scrolling grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="flex relative" style={{ minHeight: GRID_PX_HEIGHT }}>
          {/* Hour labels column */}
          <div
            style={{ width: HOUR_LABEL_WIDTH, height: GRID_PX_HEIGHT }}
            className="flex-shrink-0 border-r border-neutral-800 relative"
          >
            {hours.map((h, i) => (
              <div
                key={h}
                className="absolute right-2 text-[10px] tabular-nums text-neutral-500 font-light -translate-y-1/2"
                style={{ top: i * PX_PER_HOUR }}
              >
                {formatHourLabel(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((d) => {
            const key = format(d, 'yyyy-MM-dd')
            const items = timedByDay[key] || []
            const showGhostHere = ghost && ghost.dayKey === key
            return (
              <div
                key={`col-${key}`}
                className={
                  'flex-1 relative border-r border-neutral-800 last:border-r-0 ' +
                  (isToday(d) ? 'bg-neutral-900/30' : '')
                }
                style={{ height: GRID_PX_HEIGHT }}
              >
                {/* Horizontal hour lines */}
                {hours.map((_, i) => (
                  <div
                    key={i}
                    className="absolute left-0 right-0 border-t border-neutral-800/70"
                    style={{ top: i * PX_PER_HOUR }}
                  />
                ))}

                {/* Live "now" indicator */}
                {isSameDay(d, new Date()) && nowMinutes !== null && (
                  <div
                    className="absolute left-0 right-0 h-px bg-red-500 z-10 pointer-events-none"
                    style={{ top: nowMinutes * PX_PER_MINUTE }}
                  >
                    <div className="absolute -left-1 -top-1 w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                )}

                {/* Blocks */}
                {items.map((b) => {
                  const starts = parseServerDate(b.starts_at)!
                  const ends = parseServerDate(b.ends_at) || deriveEnds(starts, b.duration_minutes)
                  const pos = computeBlockGridPosition(starts, ends)
                  return (
                    <BlockTile
                      key={b.id}
                      block={b}
                      topPx={pos.topPx}
                      heightPx={pos.heightPx}
                      universeColor={universeColorMap.get(b.universe_id) || null}
                      active={b.id === activeBlockId}
                      isDragging={drag?.committed && drag.blockId === b.id}
                      onMouseDownBlock={onBlockMouseDown}
                    />
                  )
                })}

                {/* Drag ghost — rendered inside the target column so it
                    inherits the column's flex width without separate math. */}
                {showGhostHere && draggedBlock && (
                  <div
                    className="absolute left-1 right-1 rounded-sm border border-neutral-300/60 bg-neutral-900/80 pointer-events-none z-20 shadow-lg"
                    style={{ top: ghost!.topPx, height: Math.max(ghost!.heightPx, 22) }}
                  >
                    <div
                      className="absolute top-0 bottom-0 left-0 w-[3px]"
                      style={{
                        backgroundColor:
                          universeColorMap.get(draggedBlock.universe_id) || 'transparent',
                      }}
                    />
                    <div className="h-full pl-[7px] pr-1.5 py-0.5 flex flex-col justify-start overflow-hidden">
                      <div className="text-[9px] tabular-nums text-neutral-300 font-light leading-tight">
                        {ghost!.timeLabel}
                      </div>
                      <div className="text-[11px] font-light text-neutral-100 leading-snug line-clamp-2 mt-0.5">
                        {draggedBlock.title}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
